/**
 * OutcomesActionsTab — the "Outcomes & Actions" tab of the Accountability Registry App Pack.
 *
 * Surfaces the open ownership findings for the caller's tenant (unrepresented agents, awaiting
 * acceptance, orphaned, re-attestation due/overdue) and the one-click remediation that brings the
 * existing estate into representation. Live: GET /ownership/findings + POST /ownership/represent.
 *
 * The "Represent estate" action reuses the backend reconciliation sweep (creates NOMINATED
 * ownership records, creator = nominated owner) + the registry backfill — tenant-scoped and
 * idempotent. Per-agent findings deep-link to the agent, where its ownership actions live.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as api from '../../services/api';
import ScopeNote from './ScopeNote';
import { useToast } from '../../contexts/ToastContext';
import { Card, Table, Pill, Button, EmptyState, Skeleton } from '../../components/v2';
import { TierBadge } from './AccountabilityUI';
import { ShieldCheck, AlertTriangle, HelpCircle, Wand2, UserPlus, X } from 'lucide-react';
import ClaimOwnerSlideout from './ClaimOwnerSlideout';

function severityTone(sev) {
  switch (sev) {
    case 'high': return 'danger';
    case 'medium': return 'warning';
    default: return 'muted';
  }
}
const ACTION_LABEL = {
  REPRESENT: 'Represent estate',
  ATTEST: 'Accept Accountability',
  CLAIM: 'Nominate owner',
  REASSIGN: 'Re-assign',
  REATTEST: 'Re-attest',
};
// Static class map — Tailwind's JIT can't see template-literal class names, so the
// counter tone must resolve to a full, literal class here.
const TONE_TEXT = { danger: 'text-v2-danger', warning: 'text-v2-warning', success: 'text-v2-success' };

export default function OutcomesActionsTab({ active = true, customerId = null }) {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [view, setView] = useState(null);
  const [dashboard, setDashboard] = useState(null); // Outcomes & Actions KPI source (posture + gap MTTR)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [representing, setRepresenting] = useState(false);
  const [outcome, setOutcome] = useState(null); // last represent result
  const [attestingId, setAttestingId] = useState(null); // agentId mid-attest
  const [attestMsg, setAttestMsg] = useState(null);     // last attest/claim confirmation
  const [claimTarget, setClaimTarget] = useState(null); // finding whose owner is being nominated

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Findings drive the queue; the outcomes dashboard drives the KPI strip. The dashboard
      // call is non-fatal — if it fails the KPI strip falls back to the findings-derived counters,
      // so the queue never blanks on a dashboard hiccup.
      const [res, dRes] = await Promise.all([
        api.getOwnershipFindings(customerId),
        api.getOutcomesActions(90, customerId).catch(() => null),
      ]);
      // apiFetch wraps success as { ok, data, etag }; unwrap to the findings view.
      const data = res?.data ?? res;
      setView(data || null);
      const d = dRes?.data ?? dRes;
      setDashboard(d && d.dashboard ? d : null);
    } catch (e) {
      setError(e?.message || 'Could not load ownership findings.');
      setView(null);
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  // customerId is a real dependency: without it the memoised loader keeps the customer that
  // was current when it was created, and switching customers would silently show stale scope.
  useEffect(() => { load(); }, [load]);

  // Background refresh on re-activation (keep-alive keeps this tab mounted).
  // Skips the first activation — the mount effect above already loaded — and
  // shows no skeleton on refresh (data is retained), so switching never flashes.
  const reactivatedRef = React.useRef(false);
  useEffect(() => {
    if (!active) return;
    if (!reactivatedRef.current) { reactivatedRef.current = true; return; }
    load();
  }, [active]);

  const onRepresent = useCallback(async () => {
    setRepresenting(true);
    setError(null);
    try {
      const res = await api.representOwnershipEstate();
      const report = res?.data ?? res;
      setOutcome(report || null);
      await load();
    } catch (e) {
      setError(e?.message || 'Could not represent the estate.');
    } finally {
      setRepresenting(false);
    }
  }, [load]);

  const onAttest = useCallback(async (f) => {
    if (!f || !f.agentId) return;
    setAttestingId(f.agentId);
    setError(null);
    setAttestMsg(null);
    try {
      await api.attestOwnership(f.agentId);
      setAttestMsg(`${f.agentName || 'Agent'} — accountability accepted (now ACTIVE).`);
      addToast({ tone: 'success', title: 'Accountability accepted', description: `${f.agentName || 'Agent'} is now ACTIVE.` });
      await load();
    } catch (e) {
      // Only the nominated owner may accept — surface that clearly rather than a raw error.
      const msg = e?.status === 403
        ? `Only the nominated owner can accept accountability for ${f.agentName || 'this agent'}.`
        : (e?.message || 'Could not accept accountability.');
      setError(msg);
      addToast({ tone: 'danger', title: 'Could not accept accountability', description: msg });
    } finally {
      setAttestingId(null);
    }
  }, [load]);

  const findings = view?.findings || [];
  // Five KPIs from the reused posture aggregates + gap MTTR (honest labels; '—' when unknown).
  const kpis = useMemo(() => {
    const d = dashboard?.dashboard;
    if (!d || !d.posture) return null; // fall back to `counts` below
    const p = d.posture;
    const byState = p.inventory?.byState || {};
    const represented = p.inventory?.represented || 0;
    const current = p.attestation?.current || 0;
    const selfOwned = p.independence?.selfOwned || 0;
    const orphaned = byState.ORPHANED || 0;
    const unowned = byState.DISCOVERED_UNOWNED || 0;
    const attestPct = represented > 0 ? Math.round((current / represented) * 100) : null;
    const mttr = d.gapMttrHours;
    return [
      { label: 'Suspended \u00b7 no owner', value: orphaned, tone: orphaned > 0 ? 'danger' : 'muted' },
      { label: 'Unowned in prod', value: unowned, tone: unowned > 0 ? 'warning' : 'muted' },
      { label: 'Self-owned \u00b7 no segregation', value: selfOwned, tone: selfOwned > 0 ? 'warning' : 'muted' },
      { label: 'Attestation current', value: attestPct == null ? '\u2014' : `${attestPct}%`,
        tone: attestPct != null && attestPct < 100 ? 'warning' : 'success' },
      { label: `Gap MTTR \u00b7 ${d.windowDays || 90}d`, value: mttr == null ? '\u2014' : `${mttr}h`, tone: 'muted' },
    ];
  }, [dashboard]);
  const counts = useMemo(() => ([
    { label: 'Not represented', value: view?.unrepresented ?? 0, tone: 'danger' },
    { label: 'Awaiting acceptance', value: view?.awaitingAttestation ?? 0, tone: 'warning' },
    { label: 'Orphaned', value: view?.orphaned ?? 0, tone: 'danger' },
    { label: 'Re-attestation overdue', value: view?.attestationOverdue ?? 0, tone: 'danger' },
  ]), [view]);

  const columns = useMemo(() => ([
    {
      key: 'finding', header: 'Finding',
      render: (f) => (
        <div>
          <div className="font-medium text-v2-ink">{f.title}</div>
          <div className="text-[12px] text-v2-ink-muted mt-0.5">{f.detail}</div>
        </div>
      ),
    },
    {
      key: 'scope', header: 'Scope',
      render: (f) => (f.agentId
        ? (
          <div className="min-w-0">
            <div className="text-v2-ink truncate">{f.agentName || 'Unnamed agent'}</div>
            {/* Short agent id disambiguates same-named agents (real distinct systems). */}
            <div className="text-[11px] text-v2-ink-muted font-mono truncate">{(f.agentId || '').slice(0, 8)}</div>
          </div>
        )
        : <span className="text-v2-ink-muted">{`${f.count} agent${f.count === 1 ? '' : 's'}`}</span>),
    },
    {
      key: 'materiality', header: 'Materiality',
      render: (f) => (f.tier
        ? <TierBadge tier={f.tier} />
        : <span className="text-v2-ink-muted">—</span>),
    },
    { key: 'severity', header: 'Severity', render: (f) => <Pill tone={severityTone(f.severity)} size="sm" dot>{f.severity}</Pill> },
    {
      key: 'action', header: 'Action', align: 'right',
      render: (f) => {
        if (f.action === 'REPRESENT') {
          return (
            <Button variant="primary" size="sm" loading={representing} iconLeft={<Wand2 size={14} />} onClick={onRepresent}>
              {ACTION_LABEL.REPRESENT}
            </Button>
          );
        }
        // Attestation is a CONSENT act — only the nominated owner may accept. Everyone else sees
        // who it is waiting on, never an actionable button they cannot use.
        if ((f.action === 'ATTEST' || f.action === 'REATTEST') && f.agentId) {
          if (!f.viewerIsOwner) {
            return (
              <span className="text-[12px] text-v2-ink-muted">
                Awaiting {f.ownerName || 'owner'}
              </span>
            );
          }
          return (
            <Button variant="primary" size="sm" loading={attestingId === f.agentId}
              iconLeft={<ShieldCheck size={14} />} onClick={() => onAttest(f)}>
              {ACTION_LABEL[f.action]}
            </Button>
          );
        }
        // CLAIM — open the nomination ceremony (name the accountable owner) in place.
        if (f.action === 'CLAIM' && f.agentId) {
          return (
            <Button variant="primary" size="sm" iconLeft={<UserPlus size={14} />} onClick={() => setClaimTarget(f)}>
              {ACTION_LABEL.CLAIM}
            </Button>
          );
        }
        // REASSIGN and anything else route to the agent, where those flows live.
        return (
          <Button variant="secondary" size="sm" onClick={() => f.agentId && navigate(`/agents/${f.agentId}`)}>
            {ACTION_LABEL[f.action] || 'Open'}
          </Button>
        );
      },
    },
  ]), [representing, onRepresent, onAttest, attestingId, navigate]);

  if (loading && !view) {
    return <div className="p-2"><Skeleton variant="text" lines={8} /></div>;
  }
  // Only a LOAD failure (no data) replaces the surface; a per-row action error (attest/claim/
  // represent) is shown inline below so it never blanks the queue.
  if (error && !view) {
    return (
      <EmptyState
        icon={<HelpCircle size={22} />}
        title="Could not load Outcomes & Actions"
        description={error}
        primaryAction={<Button variant="secondary" size="sm" onClick={load}>Try again</Button>}
      />
    );
  }

  const unrepresented = view?.unrepresented ?? 0;

  return (
    <>
      <ScopeNote scope={dashboard?.posture?.scope} className="mb-2" />
      {/* Estate maintenance — represent any unrepresented agents AND normalise legacy
          auto-nominated (owner = creator) records to the correct DISCOVERED_UNOWNED ceremony. */}
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="text-[12.5px] text-v2-ink-muted">
          Represent the estate and normalise legacy auto-nominations so every agent follows the
          real ceremony: <span className="text-v2-ink">nominate an owner</span> → the owner accepts.
        </div>
        <Button variant="secondary" size="sm" loading={representing} iconLeft={<Wand2 size={14} />} onClick={onRepresent}>
          Represent &amp; normalize estate
        </Button>
      </div>

      {/* Inline action error (never blanks the queue) */}
      {error ? (
        <Card padding="sm" className="mb-4">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[12.5px] text-v2-danger">{error}</span>
            <button onClick={() => setError(null)} aria-label="Dismiss" className="text-v2-ink-muted hover:text-v2-ink"><X size={14} /></button>
          </div>
        </Card>
      ) : null}

      {/* KPI strip — 5 posture KPIs from the reused dashboard; falls back to the findings
          counters if the dashboard call failed, so the surface never blanks. */}
      {(kpis || counts) && (
      <div className={`grid grid-cols-2 ${kpis ? 'md:grid-cols-5' : 'md:grid-cols-4'} gap-3 mb-4`}>
        {(kpis || counts).map((c) => (
          <Card key={c.label} padding="sm">
            <div className="text-[12px] text-v2-ink-muted font-semibold">{c.label}</div>
            <div className={`text-[22px] font-bold mt-1 ${TONE_TEXT[c.tone] || 'text-v2-ink'}`}>{c.value}</div>
          </Card>
        ))}
      </div>
      )}

      {/* Represent-estate call to action */}
      {unrepresented > 0 ? (
        <Card padding="md" className="mb-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-v2-md bg-v2-danger-soft text-v2-danger flex items-center justify-center flex-shrink-0">
              <AlertTriangle size={18} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-[14px] font-semibold text-v2-ink">
                {`${unrepresented} agent${unrepresented === 1 ? '' : 's'} in this tenant have no accountable owner`}
              </div>
              <div className="text-[12.5px] text-v2-ink-muted mt-0.5">
                Represent the estate to enter each agent into the lifecycle as DISCOVERED_UNOWNED (no presumed
                owner), then nominate an accountable owner and have them accept. Idempotent.
              </div>
            </div>
            <Button variant="primary" size="sm" loading={representing} iconLeft={<Wand2 size={15} />} onClick={onRepresent}>
              Represent estate
            </Button>
          </div>
        </Card>
      ) : null}

      {/* Last outcome */}
      {outcome ? (
        <Card padding="sm" className="mb-4">
          <div className="flex items-center gap-2 text-[13px]">
            <ShieldCheck size={16} className="text-v2-success" />
            <span className="text-v2-ink">
              {`Represented ${outcome.represented ?? 0} agent${(outcome.represented ?? 0) === 1 ? '' : 's'}`}
              {typeof outcome.alreadyRepresented === 'number' ? ` · ${outcome.alreadyRepresented} already represented` : ''}
              {typeof outcome.scanned === 'number' ? ` · ${outcome.scanned} scanned` : ''}.
            </span>
          </div>
        </Card>
      ) : null}

      {/* Last attestation confirmation */}
      {attestMsg ? (
        <Card padding="sm" className="mb-4">
          <div className="flex items-center gap-2 text-[13px]">
            <ShieldCheck size={16} className="text-v2-success" />
            <span className="text-v2-ink">{attestMsg}</span>
          </div>
        </Card>
      ) : null}

      {/* Actions queue */}
      <Card padding="none">
        {findings.length === 0 ? (
          <EmptyState
            icon={<ShieldCheck size={22} />}
            title="No open ownership findings"
            description="Every agent in scope has an accountable owner and current attestation."
          />
        ) : (
          <Table columns={columns} rows={findings} rowKey={(f, i) => `${f.type}:${f.agentId || 'agg'}:${i}`} ariaLabel="Ownership findings" />
        )}
      </Card>

      <ClaimOwnerSlideout
        finding={claimTarget}
        onClose={() => setClaimTarget(null)}
        onClaimed={(msg) => { setClaimTarget(null); setAttestMsg(msg); addToast({ tone: 'success', title: 'Owner nominated', description: msg }); load(); }}
      />
    </>
  );
}
